import { Col, Flex, Row, message } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import HeaderCard from 'components/HeaderCard';
import Select from 'components/Select';
import PipeReviewTable from './components/PipeReviewTable';

import { PICTURE_STATUS, PIPE_RELATED_TASK_TYPES } from 'utils/constants';

import AlertBar from 'components/AlertBar';
import SelectComponent from 'components/Select';
import RBCountryFilter, {
  useRoleBasedCountryFilter,
} from 'components/UniversalFilters/RBCountryFilter';
import useSeasonIndex from 'hooks/useSeasonIndex';
import { useTranslation } from 'react-i18next';
import useFarmerGroup from 'screens/farmer-groups/hooks/useFarmerGroupListQuery';
import PipeReviewDashboardWidget from './components/PipeReviewDashboardWidget';
import { getPipeTask } from './helper';
import usePipeListQuery from './hooks/usePipeListQuery';
import useUpdatePipeMutation from './hooks/useUpdatePipeMutation';
import './styles.scss';

const PipeReview = () => {
  // Table Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [highlighted, setHighlighted] = useState<string | null>(null);
  // Filters
  const [farmerGroup, setFarmerGroup] = useState<string>();
  const [searchFarmerGroup, setSearchFarmerGroup] = useState<string>();
  const [status, setStatus] = useState<string>();
  const [task, setTask] = useState<string>();
  const [country, setCountry] = useRoleBasedCountryFilter();
  const [seasonIndex, setSeasonIndex] = useState<string>();
  // Hooks
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const documentId = searchParams.get('documentId');

  const params = useMemo(
    () => ({
      page: currentPage,
      size: pageSize,
      seasonId: seasonIndex,
      country: country,
      task,
      farmerGroup,
      status,
    }),
    [currentPage, pageSize, seasonIndex, country, task, farmerGroup, status],
  );

  const { data, totalCount, isLoading, error } = usePipeListQuery(params);

  const { data: farmerGroupData, isLoading: isFarmerGroupLoading } =
    useFarmerGroup({ country: country, search: searchFarmerGroup, size: 20 });

  const { seasonIndexFilterOptions, currentSeason } = useSeasonIndex(country);

  const farmerGroupOptions = farmerGroupData?.data?.content.map((val) => ({
    value: val.name,
    label: val.name,
  }));

  const { mutate: mutatePipeStatus, isLoading: isLoadingChangeStatus } =
    useUpdatePipeMutation({
      onSuccess: () => message.success('Picture reviewed successfully'),
      onError: () =>
        message.error('Failed to review picture. Please try again.'),
    });

  const handleDocumentAction = (
    id: string,
    task: string,
    status: 'APPROVED' | 'REJECTED',
    reason: string,
  ) => {
    mutatePipeStatus({
      mrvResourceId: id,
      status,
      type: getPipeTask(task),
      reason,
    });
  };

  const handlePageChange = (page: number, pageSize: number) => {
    setCurrentPage(page);
    setPageSize(pageSize);
  };

  const tableTopRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    tableTopRef?.current?.scrollIntoView({ behavior: 'smooth' });
  }, [currentPage]);

  useEffect(() => {
    if (documentId != null) {
      setHighlighted(documentId);
    }
  }, [documentId]);

  useEffect(() => {
    if (currentSeason) {
      setSeasonIndex(currentSeason.id);
    }
  }, [currentSeason]);

  return (
    <Flex vertical className="pipe-review-container" ref={tableTopRef}>
      <HeaderCard
        filters={
          <Flex gap={20} align="center" wrap="wrap">
            <RBCountryFilter selectedCountry={country} onChange={setCountry} />
            <SelectComponent
              label={t('common.filters.season')}
              placeholder={t('common.filters.seasonPlaceholder')}
              value={seasonIndex}
              options={seasonIndexFilterOptions}
              onChange={setSeasonIndex}
            />
          </Flex>
        }
        headerLabel={t('pipeReview.title')}
        headerInfo={t('pipeReview.reviewPicturesOf')}
      />
      <PipeReviewDashboardWidget seasonIndex={seasonIndex} />
      <Row className="table-container">
        <Col span={24}>
          <Row className="filters">
            <Select
              allowClear
              label={t('pipeReview.status')}
              value={status}
              options={PICTURE_STATUS}
              placeholder={t('pipeReview.selectPictureStatus')}
              onChange={setStatus}
            />

            <Select
              allowClear
              label={t('pipeReview.task')}
              value={task}
              placeholder={t('pipeReview.selectTask')}
              options={PIPE_RELATED_TASK_TYPES}
              onChange={setTask}
            />
            <Select
              label={t('pipeReview.farmerGroup')}
              allowClear
              showSearch
              onSearch={(value) => setSearchFarmerGroup(value)}
              placeholder={t('pipeReview.selectGroupName')}
              loading={isFarmerGroupLoading}
              options={farmerGroupOptions}
              value={farmerGroup}
              onChange={setFarmerGroup}
            />
          </Row>
        </Col>

        <Col span={24}>
          {error && (
            <AlertBar
              message={error?.message}
              type="error"
              className="mb-4"
              clearAfter={3000}
            />
          )}
          <PipeReviewTable
            isDocumentActionLoading={isLoadingChangeStatus}
            isLoading={isLoading}
            data={data?.data}
            pagination={{
              total: totalCount,
              currentPage: currentPage,
              pageSize: pageSize,
              onChange: handlePageChange,
              handlePageChange: handlePageChange,
            }}
            onDocumentAction={handleDocumentAction}
            highlighted={highlighted}
          />
        </Col>
      </Row>
    </Flex>
  );
};

export default PipeReview;
